import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { apiMessage } from '@/hooks/offers/api-message'
import { adminTenantService } from '@/services/admin-tenant.service'

/** The field's refusal for anything but a whole number 1–365 or empty (TO-125 BR-10). */
export const DEFAULT_EXPIRY_INVALID = 'Enter a whole number of days from 1 to 365, or leave it empty.'

/** `''` → null (no default); a whole number 1–365 → that number; anything else → undefined (invalid). */
export const parseDefaultExpiryDays = (text: string): number | null | undefined => {
	const trimmed = text.trim()
	if (trimmed === '') return null
	if (!/^\d+$/.test(trimmed)) return undefined
	const days = Number(trimmed)
	return days >= 1 && days <= 365 ? days : undefined
}

/**
 * The console's GENERAL section for a Super-Admin (TO-125 REQ-109): reads the
 * tenant (`GET /admin/tenants/{tenantId}`) and saves its default offer expiry
 * (`PATCH … { defaultExpirationDays }`). An invalid value is refused here, with
 * no request; a server refusal is shown and the typed value kept.
 */
export function useTenantGeneralSettings(tenantId: string, enabled: boolean) {
	const client = useQueryClient()
	const key = ['admin-tenant', tenantId] as const
	const tenant = useQuery({ queryKey: key, queryFn: () => adminTenantService.get(tenantId), enabled, retry: false })
	const [days, setDays] = useState('')
	const [fieldError, setFieldError] = useState<string | null>(null)
	const stored = tenant.data?.defaultExpirationDays
	useEffect(() => {
		if (stored !== undefined) setDays(stored === null ? '' : String(stored))
	}, [stored])

	const save = useMutation({
		mutationFn: (value: number | null) => adminTenantService.updateDefaultExpirationDays(tenantId, value),
		onSuccess: (saved) => client.setQueryData(key, saved),
	})

	return {
		loading: tenant.isPending,
		loadFailed: tenant.isError,
		reload: () => void tenant.refetch(),
		days,
		fieldError,
		saveError: save.error ? (apiMessage(save.error) ?? 'The default expiry could not be saved.') : null,
		saving: save.isPending,
		onChange: (text: string) => {
			setDays(text)
			setFieldError(null)
		},
		onSave: () => {
			const value = parseDefaultExpiryDays(days)
			if (value === undefined) {
				setFieldError(DEFAULT_EXPIRY_INVALID)
				return
			}
			save.mutate(value)
		},
	}
}
